import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { dashboardAPI } from '@/utils/api';
import api from '@/utils/api';
import { formatINR, formatUSD } from '@/utils/formatters';
import {
  LayoutDashboard, TrendingUp, TrendingDown, DollarSign, Users, FolderOpen,
  FileText, Clock, AlertTriangle, ArrowRight, CheckCircle2, RefreshCw,
  BarChart3, IndianRupee, Briefcase, Layers
} from 'lucide-react';
import { toast } from 'sonner';

const OwnerDashboard = () => {
  const { user } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectStats, setProjectStats] = useState(null);
  const [billingStats, setBillingStats] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  
  useEffect(() => { loadData(); }, []);
  
  const loadData = async () => {
    try {
      const [statsRes, remindersRes, projRes, summaryRes, teamRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getReminders(),
        api.get('/client-projects').catch(() => ({ data: [] })),
        api.get('/owner/financial-summary').catch(() => ({ data: {} })),
        api.get('/team-projects').catch(() => ({ data: [] })),
      ]);
      
      setStats(statsRes.data);
      setReminders(remindersRes.data);
      
      const projects = projRes.data || [];
      setProjectStats({
        total: projects.length,
        completed: projects.filter(p => p.task_status === 'completed').length,
        inProgress: projects.filter(p => p.task_status === 'in progress').length,
      });
      
      setBillingStats(summaryRes.data);
      
      const team = teamRes.data || [];
      setTeamStats({
        totalResources: team.length,
        active: team.filter(t => t.in_progress > 0).length,
        topResource: team[0]?.resource || 'N/A',
        topMinutes: team[0]?.total_minutes || 0,
      });
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const completionPct = projectStats ? Math.round((projectStats.completed / Math.max(projectStats.total, 1)) * 100) : 0;

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Overview of your business data</p>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xs text-muted-foreground hidden md:block">Last updated: {new Date().toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            <Button variant="outline" size="sm" onClick={loadData} className="rounded-xl">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Hero Stat Cards — Gradient */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg shadow-blue-500/20" data-testid="stat-total-projects">
            <div className="flex items-center justify-between mb-4">
              <Layers className="w-8 h-8 opacity-80" />
              <Badge className="bg-white/20 text-white border-0 text-[10px] uppercase tracking-wider">Projects</Badge>
            </div>
            <p className="text-4xl font-bold">{projectStats?.total || 0}</p>
            <p className="text-sm text-white/70 mt-1">Total Projects</p>
          </div>

          <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-emerald-500 to-teal-700 shadow-lg shadow-emerald-500/20" data-testid="stat-completed">
            <div className="flex items-center justify-between mb-4">
              <CheckCircle2 className="w-8 h-8 opacity-80" />
              <Badge className="bg-white/20 text-white border-0 text-[10px] uppercase tracking-wider">Done</Badge>
            </div>
            <p className="text-4xl font-bold">{projectStats?.completed || 0}</p>
            <p className="text-sm text-white/70 mt-1">{completionPct}% complete</p>
          </div>

          <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-orange-400 to-amber-600 shadow-lg shadow-orange-400/20" data-testid="stat-in-progress">
            <div className="flex items-center justify-between mb-4">
              <Clock className="w-8 h-8 opacity-80" />
              <Badge className="bg-white/20 text-white border-0 text-[10px] uppercase tracking-wider">Active</Badge>
            </div>
            <p className="text-4xl font-bold">{projectStats?.inProgress || 0}</p>
            <p className="text-sm text-white/70 mt-1">In Progress</p>
          </div>

          <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-violet-500 to-purple-700 shadow-lg shadow-violet-500/20" data-testid="stat-total-revenue">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="w-8 h-8 opacity-80" />
              <Badge className="bg-white/20 text-white border-0 text-[10px] uppercase tracking-wider">Revenue</Badge>
            </div>
            <p className="text-4xl font-bold">{formatUSD(billingStats?.total_income || 0)}</p>
            <p className="text-sm text-white/70 mt-1">{billingStats?.billing_count || 0} billings</p>
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Card className="hover:shadow-md transition-all duration-200 group cursor-pointer" onClick={() => window.location.href='/admin/contributors'}>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
                <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-3xl font-bold">{teamStats?.totalResources || 0}</p>
              <p className="text-sm text-muted-foreground mt-1">Active Resources</p>
              <p className="text-xs text-muted-foreground">Top: {teamStats?.topResource}</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-all duration-200 group cursor-pointer" onClick={() => window.location.href='/owner/finance'}>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
                <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-3xl font-bold">{formatUSD(billingStats?.profit || 0)}</p>
              <p className="text-sm text-muted-foreground mt-1">Net Profit</p>
              <p className="text-xs text-muted-foreground">{billingStats?.margin?.toFixed(1) || 0}% margin</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-all duration-200 group cursor-pointer" onClick={() => window.location.href='/owner/payments'}>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
                <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-3xl font-bold">{formatUSD(billingStats?.unaccepted_amount || 0)}</p>
              <p className="text-sm text-muted-foreground mt-1">Pending Acceptance</p>
              <p className="text-xs text-muted-foreground">{billingStats?.unaccepted_count || 0} invoices</p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-all duration-200 group cursor-pointer" onClick={() => window.location.href='/owner/billings'}>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center"><FileText className="w-5 h-5 text-violet-600" /></div>
                <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-3xl font-bold">{billingStats?.invoice_count || 0}</p>
              <p className="text-sm text-muted-foreground mt-1">Total Invoices</p>
              <p className="text-xs text-muted-foreground">{formatINR(stats?.total_expense || 0)} expenses</p>
            </CardContent>
          </Card>
        </div>

        {/* Alerts */}
        {billingStats?.unaccepted_count > 0 && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="font-semibold text-amber-800">Pending Acceptance — {billingStats.unaccepted_count} invoice(s)</p>
                <p className="text-sm text-amber-600">Total unaccepted amount: {formatUSD(billingStats.unaccepted_amount)}</p>
              </div>
            </div>
            <Link to="/owner/billings"><Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100">View All</Button></Link>
          </div>
        )}

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Task Completion */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet-500" /> Task Completion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-3">
                <span className="text-4xl font-bold">{completionPct}%</span>
              </div>
              <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-blue-500 transition-all duration-700" style={{ width: `${completionPct}%` }} />
              </div>
              <div className="flex justify-between mt-3 text-sm">
                <span className="text-muted-foreground">{projectStats?.completed || 0} completed</span>
                <span className="text-muted-foreground">{projectStats?.inProgress || 0} remaining</span>
              </div>
              <div className="flex gap-3 mt-4">
                <div className="flex-1 p-3 rounded-xl bg-emerald-50">
                  <p className="text-xs text-emerald-600 font-medium">Verified</p>
                  <p className="text-lg font-bold text-emerald-700">{projectStats?.completed || 0}</p>
                </div>
                <div className="flex-1 p-3 rounded-xl bg-amber-50">
                  <p className="text-xs text-amber-600 font-medium">Pending</p>
                  <p className="text-lg font-bold text-amber-700">{projectStats?.inProgress || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2"><IndianRupee className="w-4 h-4 text-emerald-500" /> Financial Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-blue-50 text-center">
                  <p className="text-xs text-blue-600 font-medium mb-1">Total Income</p>
                  <p className="text-lg font-bold text-blue-700">{formatUSD(billingStats?.total_income || 0)}</p>
                  <p className="text-[10px] text-blue-500">{billingStats?.billing_count || 0} billings</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50 text-center">
                  <p className="text-xs text-emerald-600 font-medium mb-1">Profit</p>
                  <p className="text-lg font-bold text-emerald-700">{formatUSD(billingStats?.profit || 0)}</p>
                  <p className="text-[10px] text-emerald-500">{billingStats?.margin?.toFixed(1) || 0}% margin</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50 text-center">
                  <p className="text-xs text-amber-600 font-medium mb-1">Outstanding</p>
                  <p className="text-lg font-bold text-amber-700">{formatUSD(billingStats?.unaccepted_amount || 0)}</p>
                  <p className="text-[10px] text-amber-500">{billingStats?.unaccepted_count || 0} pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OwnerDashboard;
